import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getRequest } from '../Helper/ApiClient';
import { useSentry } from '../../../SentryConfig';

export interface User {
  id: number;
  name: string;
  email: string;
  erp_unpaid_sums: number;
  favourites_count: number;
  ROLE_ALLOWED_TO_SWITCH?: boolean;
  ROLE_PREVIOUS_ADMIN?: boolean;
  is_exhibitor?: boolean;
  has_api_access?: boolean;
  bonus?: any;
  vts_orders_visible?: boolean;
  allow_upload_release_basket_qty?: boolean;
  prepay: boolean;
  invoice_country_erp_id?: number;
  invoice_country_name?: string;
  is_employee: boolean;
  dashboard_enabled?: boolean;
}

export interface BasketHeaderInfo {
  basket_total_quantity: number;
  basket_total_price: number;
  // The distinct ERP ids of the items (product sizes) and flavours (product
  // attributes) in the basket, from the positions the totals count. The
  // customer dashboard leaves them out of "Order again" and "Suggested for
  // you". Optional: an older answer does not send them.
  basket_product_size_erp_ids?: number[];
  basket_product_attribute_erp_ids?: number[];
}

interface IUserContext {
  user: User | null;
  basketInfo: BasketHeaderInfo | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  loading: boolean;
  isError: boolean;
  isSuccess: boolean;
  fetchUser: () => void;
  updateFavouritesCount: (value: number) => void;
}

const UserContext = createContext<IUserContext>({
  user: null,
  basketInfo: null,
  // @ts-ignore
  setUser: null,
  // @ts-ignore
  fetchUser: null,
  loading: false,
  isError: false,
  isSuccess: false,
  // @ts-ignore
  updateFavouritesCount: null,
});

export function UserProvider({ children }: any) {
  const [user, setUser] = useState<User | null>(null);
  const [basketInfo, setBasketInfo] = useState<BasketHeaderInfo | null>(null);

  const { data, isSuccess, isLoading, isError, refetch } = getRequest<{
    user: User | null;
    basket_info: BasketHeaderInfo | null;
  }>('/api/current_user');

  useSentry({ user: data?.user });

  useEffect(() => {
    if (isSuccess && data) {
      setUser(data.user);
      setBasketInfo(data.basket_info);
    } else {
      setUser(null);
      setBasketInfo(null);
    }
  }, [data]);

  const fetchUser = () => {
    refetch();
  };

  const updateFavouritesCount = (value: number) => {
    if (user) {
      setUser({ ...user, favourites_count: value });
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        basketInfo,
        fetchUser,
        loading: isLoading,
        isError: isError,
        isSuccess: isSuccess,
        updateFavouritesCount,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}
